import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { Search } from "lucide-react";
import AddButton from "../../../buttons/button";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import LoadingSpinner from "../../../helper-components/loading-spinner";
import {
  itemCategoryBulkPOListAction,
  itemCategoryBulkPOUpdateAction,
  itemsSalesSearchBulkPOAction,
  itemBulkPOUpdateAction,
  itemsSalesByMoleculeBulkPOAction,
} from "../../../../redux/actions/purchase-action";
import { compositionSearchListAction } from "../../../../redux/actions/settings-action";

const SALES_PERIODS = [
  { label: "Last 60 Days", value: "60" },
  { label: "Last 90 Days", value: "90" },
  { label: "Last 180 Days", value: "180" },
];

const MIN_SEARCH_LENGTH = 5;
const SEARCH_DEBOUNCE_MS = 400;

const toNumber = (value) => {
  const num = Number(value);
  return value === "" || value === null || value === undefined || Number.isNaN(num)
    ? 0
    : num;
};

// mirrors the backend: purchasing_value = ceil(sold + sold * (div% / 100))
const computePurchasingValue = (sold, divPercent) => {
  const soldNum = toNumber(sold);
  const div = toNumber(divPercent);
  return Math.ceil(soldNum + (soldNum * div) / 100);
};

const formatCalc = (sold, div) => {
  const soldNum = toNumber(sold);
  const divNum = toNumber(div);
  const sign = divNum >= 0 ? "+" : "";
  return `${soldNum} ${sign}${divNum}% = ${computePurchasingValue(soldNum, divNum)}`;
};

const ItemInventorySetupTable = () => {
  const dispatch = useDispatch();

  const [salesPeriod, setSalesPeriod] = useState("180");
  const [globalMin, setGlobalMin] = useState("");
  const [globalMax, setGlobalMax] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // null | "category" | "search" | "molecule"
  const [mode, setMode] = useState(null);
  const [rows, setRows] = useState([]);

  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const loading = categoriesLoading || itemsLoading;
  const requestIdRef = useRef(0);

  const [moleculeOptions, setMoleculeOptions] = useState([]);
  const [moleculeLoading, setMoleculeLoading] = useState(false);
  const [selectedMolecule, setSelectedMolecule] = useState(null);

  useEffect(() => {
    setMoleculeLoading(true);
    dispatch(
      compositionSearchListAction(
        "",
        (data) => {
          setMoleculeOptions(
            (data?.data?.list || []).map((item) => ({
              label: item?.name,
              value: item?.id,
            }))
          );
          setMoleculeLoading(false);
        },
        () => setMoleculeLoading(false)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGlobalChange = (field, value) => {
    if (field === "min") {
      setGlobalMin(value);
    } else {
      setGlobalMax(value);
    }
    if (value === "") return;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return;
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        [field === "min" ? "min_div" : "max_div"]: numericValue,
      }))
    );
  };

  const handleRowChange = (id, field, value) => {
    const numericValue = value === "" ? "" : Number(value);
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: numericValue } : row))
    );
  };

  const mapRows = (list, idKey, nameKey) =>
    (list || []).map((item) => ({
      id: item[idKey],
      name: item[nameKey],
      sold: item.qty_sold,
      min_div: toNumber(globalMin),
      max_div: toNumber(globalMax),
    }));

  const fetchCategories = () => {
   const requestId = ++requestIdRef.current;
    setRows([]);
    setCategoriesLoading(true);
    setItemsLoading(false);
    dispatch(
      itemCategoryBulkPOListAction(
        { days: Number(salesPeriod) },
        (data) => {
          if (requestIdRef.current !== requestId) return;
          setRows(mapRows(data?.data?.categoryList, "category_id", "category_name"));
          setCategoriesLoading(false);
        },
        (err) => {
          if (requestIdRef.current !== requestId) return;
          setCategoriesLoading(false);
          setRows([]);
          errorToast(err?.data?.message);
        }
      )
    );
  };

  const handleLoadCategories = () => {
    setSelectedMolecule(null);
    setSearchValue("");
    setMode("category");
    fetchCategories();
  };

  const fetchMoleculeItems = (moleculeId) => {
    const requestId = ++requestIdRef.current;
    setRows([]);
    setItemsLoading(true);
    setCategoriesLoading(false);
    dispatch(
      itemsSalesByMoleculeBulkPOAction(
        { days: Number(salesPeriod), molecule_id: moleculeId },
        (data) => {
          if (requestIdRef.current !== requestId) return;
          setRows(mapRows(data?.data?.itemList, "item_id", "item_name"));
          setItemsLoading(false);
        },
        (err) => {
          if (requestIdRef.current !== requestId) return;
          setItemsLoading(false);
          setRows([]);
          errorToast(err?.data?.message);
        }
      )
    );
  };

  const handleMoleculeSelect = (option) => {
    setSelectedMolecule(option || null);
    setSearchValue("");
    if (!option) {
      requestIdRef.current += 1;
      setMode(null);
      setRows([]);
      setCategoriesLoading(false);
      setItemsLoading(false);
      return;
    }
    setMode("molecule");
    fetchMoleculeItems(option.value);
  };

  const fetchSearchItems = (query) => {
    const requestId = ++requestIdRef.current;
    setRows([]);
    setItemsLoading(true);
    setCategoriesLoading(false);
    dispatch(
      itemsSalesSearchBulkPOAction(
        { days: Number(salesPeriod), search: query },
        (data) => {
          if (requestIdRef.current !== requestId) return;
          setRows(mapRows(data?.data?.itemList, "item_id", "item_name"));
          setItemsLoading(false);
        },
        (err) => {
          if (requestIdRef.current !== requestId) return;
          setItemsLoading(false);
          setRows([]);
          errorToast(err?.data?.message);
        }
      )
    );
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setSelectedMolecule(null);
    if (value.trim().length === 0) {
      requestIdRef.current += 1;
      setMode(null);
      setRows([]);
      setCategoriesLoading(false);
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    const trimmed = searchValue.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      requestIdRef.current += 1;
      setItemsLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      setMode("search");
      fetchSearchItems(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (mode === "category") {
      fetchCategories();
    } else if (mode === "molecule" && selectedMolecule) {
      fetchMoleculeItems(selectedMolecule.value);
    } else if (mode === "search" && searchValue.trim().length >= MIN_SEARCH_LENGTH) {
      fetchSearchItems(searchValue.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPeriod]);

  const handleSave = () => {
    if (!rows.length || !mode || saving || loading) return;
    setSaving(true);
    const onSaveSuccess = (data) => {
      setSaving(false);
      successToast(data?.message || "Item purchasing limits updated successfully");
    };
    const onSaveError = (err) => {
      setSaving(false);
      errorToast(err?.data?.message);
    };

    if (mode === "category") {
      dispatch(
        itemCategoryBulkPOUpdateAction(
          {
            days: Number(salesPeriod),
            categories: rows.map((row) => ({
              category_id: row.id,
              min_div: toNumber(row.min_div),
              max_div: toNumber(row.max_div),
            })),
          },
          onSaveSuccess,
          onSaveError
        )
      );
    } else {
      dispatch(
        itemBulkPOUpdateAction(
          {
            days: Number(salesPeriod),
            items: rows.map((row) => ({
              item_id: row.id,
              min_div: toNumber(row.min_div),
              max_div: toNumber(row.max_div),
            })),
          },
          onSaveSuccess,
          onSaveError
        )
      );
    }
  };

  const nameHeading = mode === "category" ? "Category Name" : "Item Name";
  const tableHeading = [nameHeading, "Sold", "Min", "Max"];
  const searchTooShort =
    mode !== "category" &&
    searchValue.trim().length > 0 &&
    searchValue.trim().length < MIN_SEARCH_LENGTH;

  return (
    <div className="flex flex-col pt-3" style={{ height: "70vh" }}>
      <div className="mb-4">
        <p className="text-sm fw_700 text-secondary">Item Sales &amp; Stock</p>
        <p className="text-xs text-secondary">
          Review sales velocity and manage min/max stock thresholds
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
            Sales Period
          </p>
          <div className="flex items-center gap-2 rounded-md border border-[var(--search-input-border)] p-1 w-fit">
            {SALES_PERIODS.map((period) => (
              <button
                type="button"
                key={period.value}
                onClick={() => setSalesPeriod(period.value)}
                className={`px-3 py-1.5 rounded text-xs whitespace-nowrap ${
                  salesPeriod === period.value
                    ? "bg-[var(--primary-button-bg-color)] text-[var(--primary-button-fg-color)] fw_700"
                    : "text-secondary fw_500"
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-end gap-4">
          <div>
            <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
              Global Min %
            </p>
            <input
              type="number"
              placeholder="Min %"
              value={globalMin}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => handleGlobalChange("min", e.target.value)}
              className="border border-[var(--pay-met-input-border)] rounded-md px-2 py-[7px] text-sm w-20 focus:outline-none focus:ring focus:ring-blue-500"
            />
          </div>
          <div>
            <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
              Global Max %
            </p>
            <input
              type="number"
              placeholder="Max %"
              value={globalMax}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => handleGlobalChange("max", e.target.value)}
              className="border border-[var(--pay-met-input-border)] rounded-md px-2 py-[7px] text-sm w-20 focus:outline-none focus:ring focus:ring-blue-500"
            />
          </div>

          <div>
            <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
              &nbsp;
            </p>
            <AddButton
              onclkFn={handleLoadCategories}
              prefixIcon={false}
              text="Load Categories"
              type="button"
              loading={categoriesLoading}
              isDisabled={loading}
              className="flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-sm fw_600 h-[2.5rem]"
            />
          </div>

          <div>
            <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
              Molecule Search
            </p>
            <Select
              isClearable
              isLoading={moleculeLoading}
              options={moleculeOptions}
              value={selectedMolecule}
              onChange={handleMoleculeSelect}
              placeholder="Search molecule..."
              menuPortalTarget={document.body}
              styles={{
                container: (base) => ({ ...base, minWidth: "14rem" }),
                control: (base) => ({
                  ...base,
                  minHeight: "2.5rem",
                  borderColor: "var(--search-input-border)",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              className="text-sm"
            />
          </div>
        </div>

        <div>
          <p className="text-[10px] fw_600 text-secondary uppercase tracking-wide mb-1">
            Search
          </p>
          <div
            style={{ minWidth: "16rem", height: "2.5rem" }}
            className="border border-[var(--search-input-border)] flex justify-between items-center rounded-md"
          >
            <input
              placeholder="Item code or name..."
              className="table_search_input px-3 py-2 !border-none !outline-none !ring-0 text-sm w-full"
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <span className="px-2">
              <Search size="16" color="#9CA3AF" />
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="mb-auto overflow-auto rounded-xl table-container border border-gray-200">
          <table className="w-full">
            <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
              <tr className="relative h-[50px]">
                {tableHeading.map((heading) => (
                  <th
                    className="text-start px-[8px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]"
                    key={heading}
                  >
                    {heading?.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableHeading.length}
                    className="text-center text-sm text-secondary py-6"
                  >
                    {searchTooShort
                      ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
                      : "No items found"}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="bg-[var(--list-table-row-bg)] h-[50px]">
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px]">
                      {row.name}
                    </td>
                    <td className="tab-cell text-sm fw_400 text-secondary px-[8px]">
                      {row.sold ?? 0}
                    </td>
                    <td className="tab-cell px-[8px]">
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          value={row.min_div}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => handleRowChange(row.id, "min_div", e.target.value)}
                          className="border border-[var(--pay-met-input-border)] rounded-md px-2 py-1.5 text-sm w-16 focus:outline-none focus:ring focus:ring-blue-500"
                        />
                        <span className="text-[10px] text-secondary whitespace-nowrap">
                          ({formatCalc(row.sold, row.min_div)})
                        </span>
                      </div>
                    </td>
                    <td className="tab-cell px-[8px]">
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          value={row.max_div}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => handleRowChange(row.id, "max_div", e.target.value)}
                          className="border border-[var(--pay-met-input-border)] rounded-md px-2 py-1.5 text-sm w-16 focus:outline-none focus:ring focus:ring-blue-500"
                        />
                        <span className="text-[10px] text-secondary whitespace-nowrap">
                          ({formatCalc(row.sold, row.max_div)})
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-auto flex gap-3 items-center justify-end w-full h-[4rem]">
        <AddButton
          onclkFn={handleSave}
          prefixIcon={false}
          text="Save"
          type="button"
          loading={saving}
          isDisabled={!rows.length || !mode || saving || loading}
          className="
    flex items-center px-3 py-2  whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-create-fs)]
    font-[var(--primary-button-font-weight)]
    "
        />
      </div>
    </div>
  );
};

export default ItemInventorySetupTable;
